import {useQuery} from '@tanstack/react-query';
import {FileViewer} from '@/matic/features/_shared/components/document-viewer';
import {
  FileViewType,
  getFileViewType,
} from '@/matic/features/vault/documents/utils/file-type-detection';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getFilesApi} from '../api/files-api';
import {filesKeys} from '../query-keys';
import type {ManagedFile} from '../types/files-types';

/** Map a managed File's stored content-type to a viewer-friendly MIME type. */
export function viewerContentType(file: ManagedFile): string | undefined {
  const vt = getFileViewType(file.contentType, file.originalFilename);
  if (vt === FileViewType.PDF) return 'application/pdf';
  if (vt === FileViewType.DOCX)
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (vt === FileViewType.MARKDOWN) return 'text/markdown';
  return file.contentType;
}

type FileDocumentViewerProps = {
  file: ManagedFile;
  className?: string;
  /** Viewer header label. Defaults to the raw filename. */
  title?: string;
  /** Page to scroll to on render (1-indexed; PDF scroll mode only). */
  initialPage?: number;
  /** Incrementing key that forces a re-scroll even to the same page —
   *  Scout's citation-click idiom (see ScoutRhsPanel). */
  scrollKey?: number;
};

/**
 * The bare document render for a managed File — fetches a presigned content URL
 * and hands it to `FileViewer`. No header, no metadata, no association chrome:
 * just the document. Parents own identity/actions; this is the viewport.
 */
export function FileDocumentViewer({
  file,
  className,
  title,
  initialPage,
  scrollKey,
}: FileDocumentViewerProps) {
  const tenantId = useRequiredTenantId();
  const contentUrlQuery = useQuery({
    queryKey: filesKeys.files.contentUrl(file.id, tenantId),
    async queryFn() {
      return getFilesApi().getContentUrl(file.id);
    },
  });

  return (
    <FileViewer
      url={contentUrlQuery.data?.url}
      contentType={viewerContentType(file)}
      loading={contentUrlQuery.isLoading}
      error={contentUrlQuery.isError ? 'Failed to load file.' : undefined}
      title={title ?? file.originalFilename}
      emptyMessage="No preview available."
      className={className}
      initialPage={initialPage}
      scrollKey={scrollKey}
    />
  );
}
